import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useOrganisme } from '../context/OrganismeContext';
import { exportAdhesionsToExcel } from '../utils/excelExport';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckIcon,
  UserIcon,
  PencilIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  ClipboardIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  PhoneIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import PhoneInput from '../components/common/PhoneInput';
import HelpButton from '../components/HelpButton';
import { helpContent } from '../data/helpContent';
import { isValidSiret, normalizeSiret, SIRET_ERROR } from '../utils/siret';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [organismeFilter, setOrganismeFilter] = useState('');
  const [showOrganismeFilter, setShowOrganismeFilter] = useState(false);
  
  // Nouveaux filtres
  const [statusFilter, setStatusFilter] = useState([]);
  const [yearFilter, setYearFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState([]);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showYearFilter, setShowYearFilter] = useState(false);
  const [showServiceFilter, setShowServiceFilter] = useState(false);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAdhesionsModal, setShowAdhesionsModal] = useState(false);
  const [selectedUserAdhesions, setSelectedUserAdhesions] = useState(null);
  const { user, permissions } = useAuth();
  const { organismeActif, isSuperAdminMode } = useOrganisme();
  const navigate = useNavigate();

  // Charger les filtres sauvegardés au montage
  useEffect(() => {
    const savedFilters = localStorage.getItem('users_filters');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setStatusFilter(filters.statusFilter || []);
        setYearFilter(filters.yearFilter || '');
        setServiceFilter(filters.serviceFilter || []);
        setOrganismeFilter(filters.organismeFilter || '');
        setSearchTerm(filters.searchTerm || '');
      } catch (error) {
        console.error('Erreur lors du chargement des filtres:', error);
      }
    }
    setFiltersLoaded(true);
  }, []);

  // Sauvegarder les filtres à chaque modification
  useEffect(() => {
    if (!filtersLoaded) return; // Ne pas sauvegarder avant le chargement initial
    
    const filters = {
      statusFilter,
      yearFilter,
      serviceFilter,
      organismeFilter,
      searchTerm
    };
    localStorage.setItem('users_filters', JSON.stringify(filters));
  }, [statusFilter, yearFilter, serviceFilter, organismeFilter, searchTerm, filtersLoaded]);

  // Super admin a toujours accès, sinon vérifier les permissions
  const isSuperAdmin = user?.role === 'super_admin';
  const canAccess = isSuperAdmin || permissions?.users?.access === true;
  const canEdit = isSuperAdmin || permissions?.users?.editUsers === true;

  // Fonctions utilitaires pour les services
  const getServiceTypeLabel = (typeService) => {
    const labels = {
      'miellerie': 'Miellerie',
      'assurance_unaf': 'Services de l\'UNAF',
    };
    return labels[typeService] || typeService;
  };

  const getServiceStatusLabel = (status) => {
    const labels = {
      'actif': 'Actif',
      'en_attente_validation': 'En attente de validation',
      'en_attente_paiement': 'Paiement en attente',
      'en_attente_caution': 'Caution en attente',
      'expire': 'Expiré',
    };
    return labels[status] || status;
  };

  // Fonctions pour les icônes de statut (alignées sur le dashboard admin)
  const getStatusIcon = (status) => {
    const icons = {
      'en_attente': "⌛",
      'paiement_demande': "💳",
      'actif': "✅",
      'refuse': "❌",
      'expiree': "⚫",
      'abandonnee': "❌"
    };
    return icons[status] || "⌛";
  };

  // Fonctions pour les actions (copier ID, naviguer)
  const copyUserId = async (userId) => {
    try {
      await navigator.clipboard.writeText(userId);
      const originalTitle = document.title;
      document.title = '✓ ID copié';
      setTimeout(() => {
        document.title = originalTitle;
      }, 1000);
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
      const textArea = document.createElement('textarea');
      textArea.value = userId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const copyAdhesionId = async (adhesionId) => {
    try {
      await navigator.clipboard.writeText(adhesionId);
      const originalTitle = document.title;
      document.title = '✓ ID adhésion copié';
      setTimeout(() => {
        document.title = originalTitle;
      }, 1000);
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
      const textArea = document.createElement('textarea');
      textArea.value = adhesionId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const viewAdhesion = (adhesionId) => {
    navigate(`/adherents/${adhesionId}`);
  };

  // Fonctions pour les filtres
  const toggleStatusFilter = (status) => {
    setStatusFilter(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    // Fermer le dropdown après sélection
    setShowStatusFilter(false);
  };

  const clearStatusFilter = () => {
    setStatusFilter([]);
  };

  const toggleServiceFilter = (service) => {
    setServiceFilter(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
    // Fermer le dropdown après sélection
    setShowServiceFilter(false);
  };

  const clearServiceFilter = () => {
    setServiceFilter([]);
  };

  // Fonction pour réinitialiser tous les filtres
  const resetAllFilters = () => {
    setStatusFilter([]);
    setYearFilter('');
    setServiceFilter([]);
    setOrganismeFilter('');
    setSearchTerm('');
    
    // Fermer tous les dropdowns
    setShowStatusFilter(false);
    setShowYearFilter(false);
    setShowServiceFilter(false);
    setShowOrganismeFilter(false);
    
    // Supprimer les filtres sauvegardés
    localStorage.removeItem('users_filters');
  };

  // Fonction pour déterminer la couleur de l'année
  const getYearColor = (annee, status) => {
    const currentYear = new Date().getFullYear();
    
    // Vert si statut actif
    if (status === 'actif') {
      return 'bg-green-100 text-green-800';
    }
    
    // Gris si année antérieure, honey si année courante/future
    return annee < currentYear ? 'bg-slate-200 text-slate-700' : 'bg-honey-100 text-honey-800';
  };

  // Fonction d'export Excel
  const exportToExcel = () => {
    const includeSarDroitEntreeColumn =
      organismeActif === 'SAR'
      || organismeFilter === 'SAR'
      || filteredUsers.some((u) => u.adhesions?.some((a) => a.organisme === 'SAR'));

    exportAdhesionsToExcel(
      filteredUsers,
      getStatusLabel,
      getServiceTypeLabel,
      getServiceStatusLabel,
      { includeSarDroitEntreeColumn }
    );
  };

  // Calculer les années disponibles
  const availableYears = [...new Set(
    users.flatMap(u => u.adhesions?.map(a => a.annee) || [])
  )].sort((a, b) => b - a);

  // Calculer les services disponibles
  const availableServices = [...new Set(
    users.flatMap(u => 
      u.adhesions?.flatMap(a => 
        a.services?.map(s => s.typeService) || []
      ) || []
    )
  )];

  // Calculer les statistiques
  const totalAdherents = users.length;
  const totalActifs = users.filter(u => 
    u.adhesions?.some(a => a.status === 'actif')
  ).length;
  const totalEnAttentePaiement = users.filter(u => 
    u.adhesions?.some(a => a.status === 'paiement_demande')
  ).length;
  const totalEnAttente = users.filter(u => 
    u.adhesions?.some(a => a.status === 'en_attente')
  ).length;

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }

    if (!canAccess) {
      navigate('/admin/dashboard');
      return;
    }

    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, canAccess, organismeActif, isSuperAdminMode]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // En mode Super Admin, pas de filtre organisme
      // En mode Admin Organisme, filtrer par organismeActif
      const params = !isSuperAdminMode && organismeActif ? `?organisme=${organismeActif}` : '';
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users-management${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(u => {
        const nom = u.nom || '';
        const prenom = u.prenom || '';
        const email = u.email || '';
        return nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
               prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
               email.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    if (organismeFilter) {
      filtered = filtered.filter(u => 
        u.adhesions.some(a => a.organisme === organismeFilter)
      );
    }

    // Filtre par statut
    if (statusFilter.length > 0) {
      filtered = filtered.filter(u => 
        u.adhesions?.some(a => statusFilter.includes(a.status))
      );
    }

    // Filtre par année
    if (yearFilter) {
      filtered = filtered.filter(u => 
        u.adhesions?.some(a => a.annee.toString() === yearFilter)
      );
    }

    // Filtre par services
    if (serviceFilter.length > 0) {
      filtered = filtered.filter(u => 
        u.adhesions?.some(a => 
          a.services?.some(s => serviceFilter.includes(s.typeService))
        )
      );
    }

    setFilteredUsers(filtered);
  }, [searchTerm, organismeFilter, statusFilter, yearFilter, serviceFilter, users]);

  const getStatusLabel = (status) => {
    const labels = {
      'en_attente': 'En attente',
      'paiement_demande': 'Paiement demandé',
      'actif': 'Actif',
      'refuse': 'Refusé',
      'expiree': 'Expirée',
      'abandonnee': 'Abandonnée',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'en_attente': 'bg-yellow-100 text-yellow-800',
      'paiement_demande': 'bg-honey-100 text-honey-800',
      'actif': 'bg-green-100 text-green-800',
      'refuse': 'bg-red-100 text-red-800',
      'expiree': 'bg-slate-100 text-slate-800',
      'abandonnee': 'bg-slate-100 text-slate-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const renderUserPhones = (u) => {
    const fixe = typeof u.telephone === 'string' ? u.telephone.trim() : '';
    const mobile = typeof u.telephoneMobile === 'string' ? u.telephoneMobile.trim() : '';
    if (!fixe && !mobile) {
      return <span className="text-sm text-slate-400">-</span>;
    }
    return (
      <div className="flex flex-col gap-1 text-sm text-slate-700">
        {fixe ? (
          <span className="inline-flex items-center gap-1.5" title="Téléphone fixe">
            <PhoneIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
            <span>{fixe}</span>
          </span>
        ) : null}
        {mobile ? (
          <span className="inline-flex items-center gap-1.5" title="Téléphone mobile">
            <DevicePhoneMobileIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
            <span>{mobile}</span>
          </span>
        ) : null}
      </div>
    );
  };

  const handleEditClick = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users-management/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingUser(response.data);
      
      setEditForm({
        typePersonne: response.data.typePersonne || 'personne_physique',
        designation: response.data.designation || '',
        raisonSociale: response.data.raisonSociale || '',
        nom: response.data.nom || '',
        prenom: response.data.prenom || '',
        email: response.data.email || '',
        telephone: response.data.telephone || '',
        telephoneMobile: response.data.telephoneMobile || '',
        dateNaissance: response.data.dateNaissance ? response.data.dateNaissance.split('T')[0] : '',
        napi: response.data.napi || '',
        siret: response.data.siret || '',
        adresse: {
          rue: response.data.adresse?.rue || '',
          codePostal: response.data.adresse?.codePostal || '',
          ville: response.data.adresse?.ville || '',
        },
      });
      setShowEditModal(true);
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur:', error);
      alert('Erreur lors de la récupération des informations de l\'utilisateur');
    }
  };

  const handleSaveUser = async () => {
    if (!isValidSiret(editForm.siret, { required: false })) {
      alert(SIRET_ERROR);
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users-management/${editingUser._id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Mettre à jour la liste locale
      setUsers(prev => prev.map(u => 
        u._id === editingUser._id 
          ? { ...u, nom: editForm.nom, prenom: editForm.prenom, email: editForm.email, telephone: editForm.telephone }
          : u
      ));
      
      setShowEditModal(false);
      setEditingUser(null);
      alert('Utilisateur mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert(error.response?.data?.message || 'Erreur lors de la mise à jour de l\'utilisateur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center text-sm text-slate-500">
          <span className="font-medium text-slate-900">Abeille Réunion</span>
          <svg className="mx-2 h-5 w-5 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span>Adhésions</span>
        </div>
      </div>

      {/* Header */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Adhésions</h1>
            <p className="mt-2 text-sm text-slate-600">
              {isSuperAdminMode 
                ? 'Liste de tous les utilisateurs inscrits avec leurs adhésions'
                : `Liste des utilisateurs ayant au moins une adhésion à l'organisme ${organismeActif || 'l\'organisme sélectionné'}`
              }
            </p>
          </div>
          {!isSuperAdminMode && (
            <HelpButton 
              title={helpContent.users.title}
              sections={helpContent.users.sections}
            />
          )}
        </div>

        {/* Statistiques */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-slate-600">Total adhérents</div>
            <div className="text-2xl font-bold text-slate-900">{totalAdherents}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-slate-600">Adhérents actifs</div>
            <div className="text-2xl font-bold text-green-600">{totalActifs}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-slate-600">En attente de traitement</div>
            <div className="text-2xl font-bold text-yellow-600">{totalEnAttente}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-slate-600">En attente de paiement</div>
            <div className="text-2xl font-bold text-honey-600">{totalEnAttentePaiement}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {/* Organisme Filter - visible uniquement en mode Super Admin */}
          {isSuperAdminMode && (
            <div className="relative">
              <button
                onClick={() => setShowOrganismeFilter(!showOrganismeFilter)}
                className="inline-flex items-center px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Organisme
                {organismeFilter && (
                  <span className="ml-2 px-2 py-0.5 bg-green-500 rounded-full text-xs">
                    {organismeFilter}
                  </span>
                )}
              </button>
              
              {showOrganismeFilter && (
                <div className="absolute z-10 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-slate-900">Organisme</span>
                      {organismeFilter && (
                        <button
                          onClick={() => setOrganismeFilter('')}
                          className="text-sm text-indigo-600 hover:text-indigo-700"
                        >
                          Effacer
                        </button>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      {['SAR', 'AMAIR'].map((org) => (
                        <button
                          key={org}
                          onClick={() => { setOrganismeFilter(org); setShowOrganismeFilter(false); }}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                            organismeFilter === org
                              ? 'bg-green-50 text-green-700'
                              : 'hover:bg-slate-50 text-slate-700'
                          }`}
                        >
                          <span>{org}</span>
                          {organismeFilter === org && (
                            <CheckIcon className="h-4 w-4 text-green-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Status Filter */}
          <div className="relative">
            <button
              onClick={() => { setShowStatusFilter(!showStatusFilter); setShowYearFilter(false); setShowServiceFilter(false); setShowOrganismeFilter(false); }}
              className="inline-flex items-center px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Statut
              {statusFilter.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-indigo-600 rounded-full text-xs">
                  {statusFilter.length}
                </span>
              )}
            </button>
            
            {showStatusFilter && (
              <div className="absolute z-10 mt-2 w-64 bg-white rounded-lg shadow-lg border border-slate-200">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-900">Statut</span>
                    {statusFilter.length > 0 && (
                      <button
                        onClick={clearStatusFilter}
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        Effacer
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {['en_attente', 'paiement_demande', 'actif', 'refuse', 'expiree', 'abandonnee'].map((status) => (
                      <button
                        key={status}
                        onClick={() => toggleStatusFilter(status)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                          statusFilter.includes(status)
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span>{getStatusLabel(status)}</span>
                        {statusFilter.includes(status) && (
                          <CheckIcon className="h-4 w-4 text-indigo-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Year Filter */}
          <div className="relative">
            <button
              onClick={() => { setShowYearFilter(!showYearFilter); setShowStatusFilter(false); setShowServiceFilter(false); setShowOrganismeFilter(false); }}
              className="inline-flex items-center px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Année
              {yearFilter && (
                <span className="ml-2 px-2 py-0.5 bg-honey-500 rounded-full text-xs">
                  {yearFilter}
                </span>
              )}
            </button>
            
            {showYearFilter && (
              <div className="absolute z-10 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-900">Année</span>
                    {yearFilter && (
                      <button
                        onClick={() => setYearFilter('')}
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        Effacer
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {availableYears.map((year) => (
                      <button
                        key={year}
                        onClick={() => { setYearFilter(year.toString()); setShowYearFilter(false); }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                          yearFilter === year.toString()
                            ? 'bg-honey-50 text-honey-700'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span>{year}</span>
                        {yearFilter === year.toString() && (
                          <CheckIcon className="h-4 w-4 text-honey-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Services Filter */}
          <div className="relative">
            <button
              onClick={() => { setShowServiceFilter(!showServiceFilter); setShowStatusFilter(false); setShowYearFilter(false); setShowOrganismeFilter(false); }}
              className="inline-flex items-center px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Services
              {serviceFilter.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-green-500 rounded-full text-xs">
                  {serviceFilter.length}
                </span>
              )}
            </button>
            
            {showServiceFilter && (
              <div className="absolute z-10 mt-2 w-64 bg-white rounded-lg shadow-lg border border-slate-200">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-900">Services</span>
                    {serviceFilter.length > 0 && (
                      <button
                        onClick={clearServiceFilter}
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        Effacer
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {availableServices.map((service) => (
                      <button
                        key={service}
                        onClick={() => toggleServiceFilter(service)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                          serviceFilter.includes(service)
                            ? 'bg-green-50 text-green-700'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span>{getServiceTypeLabel(service)}</span>
                        {serviceFilter.includes(service) && (
                          <CheckIcon className="h-4 w-4 text-green-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bouton Réinitialiser les filtres */}
          {(statusFilter.length > 0 || yearFilter || serviceFilter.length > 0 || organismeFilter || searchTerm) && (
            <button
              onClick={resetAllFilters}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Réinitialiser les filtres
            </button>
          )}

          {/* Bouton Exporter */}
          <button
            onClick={exportToExcel}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Exporter Excel
          </button>

          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un utilisateur..."
                className="block w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md leading-5 bg-white placeholder-slate-500 focus:outline-none focus:placeholder-slate-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Results count */}
          <div className="text-sm text-slate-500">
            {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                  Contacts
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider hidden sm:table-cell">
                  Adhésions
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                  Création
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredUsers.map((u) => (
                <React.Fragment key={u._id}>
                  {/* Ligne principale de l'utilisateur */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          { u.typePersonne === 'personne_physique' ?
                            <UserIcon className="h-5 w-5 text-indigo-600" /> 
                            :
                            <BuildingOfficeIcon className="h-5 w-5 text-indigo-600" />
                          }                        
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-slate-900">
                            {u.typePersonne === 'personne_physique' ? (
                              <>{u.designation ? `${u.designation} ` : ''}{u.prenom} {u.nom}</>
                            ) : (
                              <>{u.raisonSociale} ({u.designation ? `${u.designation} ` : ''}{u.prenom} {u.nom})</>
                            )}
                          </div>
                          {u.role === 'admin' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              Admin
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-middle hidden lg:table-cell">
                      <div className="flex items-center gap-2">
                        <EnvelopeIcon className="h-4 w-4 text-slate-400" /> <div className="text-sm text-slate-900">{u.email}</div>
                      </div>
                      {renderUserPhones(u)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center hidden sm:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {u.adhesions.length}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-500 hidden lg:table-cell">
                      {new Date(u.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <div className="flex items-end space-x-2">
                        {canEdit && (
                          <button
                            onClick={() => handleEditClick(u._id)}
                            className="flex gap-2 items-center py-1.5 pl-1.5 pr-2 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                            title="Éditer l'utilisateur"
                          >
                            <PencilIcon className="h-4 w-4" />
                            <span className="text-xs">Modifier l'utilisateur</span>
                          </button>
                        )}
                        {isSuperAdmin && (
                          <button
                            onClick={() => copyUserId(u._id)}
                            className="flex gap-2 items-center p-1.5 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                            title="Copier l'ID utilisateur"
                          >
                            <ClipboardIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Lignes d'adhésions détaillées */}
                  {u.adhesions.map((adhesion, idx) => (
                    <React.Fragment key={`${u._id}-adhesion-${idx}`}>
                      {/* Ligne principale de l'adhésion */}
                      <tr className="bg-slate-50">
                        <td className="py-3 whitespace-nowrap">
                          <div className="flex items-center pl-8">
                            {/* <div className="flex-shrink-0 h-8 w-8 bg-slate-200 rounded-full flex items-center justify-center mr-3">
                              {getStatusIcon(adhesion.status)}
                            </div> */}
                            <div>
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="text-sm font-bold text-slate-900">
                                  {adhesion.organisme}
                                </span>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getYearColor(adhesion.annee, adhesion.status)}`}>
                                  <span className="text-xs font-bold">{adhesion.annee}</span>
                                  <span className="text-xs text-slate-400 px-1.5 pb-0.5">|</span>
                                  {getStatusLabel(adhesion.status)}
                                </span>
                                <span className="text-xs text-slate-500">{adhesion.nombreRuches ? `${adhesion.nombreRuches} ruches` : 'Nombre de ruches non renseigné'}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap hidden lg:table-cell">
                          {/* Vide */}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap hidden sm:table-cell">
                          {/* Vide */}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-center text-xs text-slate-500 hidden lg:table-cell">
                          {new Date(adhesion.createdAt).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-xs text-right">
                          <div className="flex items-center justify-end space-x-2">
                            {/* Boutons d'actions */}
                            <button
                              onClick={() => viewAdhesion(adhesion._id)}
                              className="inline-flex gap-2 items-center py-1.5 pl-1.5 pr-2 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                              title="Voir les détails de l'adhésion"
                            >
                              <EyeIcon className="h-3 w-3" />
                              Voir adhésion
                            </button>
                            {isSuperAdmin && (
                              <button
                                onClick={() => copyAdhesionId(adhesion._id)}
                                className="inline-flex items-center p-1.5 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                                title="Copier l'ID de l'adhésion"
                              >
                                <ClipboardIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Lignes des services */}
                      {adhesion.services && adhesion.services.map((service, serviceIdx) => (
                        <tr key={`${u._id}-adhesion-${idx}-service-${serviceIdx}`} className="bg-slate-50">
                          <td className="py-3 whitespace-nowrap">
                            <div className="flex items-center pl-8">
                              <div className="">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-lg text-slate-400 pb-2">&#x21B3;</span>
                                  <span className="text-sm font-bold text-slate-900">
                                    {getServiceTypeLabel(service.typeService)}
                                  </span>
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getYearColor(service.annee, service.status)}`}>
                                    <span className="text-xs font-bold">{service.annee}</span>
                                    <span className="text-xs text-slate-400 px-1.5 pb-0.5">|</span>
                                    {getServiceStatusLabel(service.status)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap hidden lg:table-cell">
                            {/* Vide */}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap hidden sm:table-cell">
                            {/* Vide */}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-center text-xs text-slate-500 hidden lg:table-cell">
                            {new Date(service.createdAt).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-xs text-right">
                            <div className="flex items-center justify-end space-x-2">
                              {/* Boutons d'actions pour services */}
                              <button
                                onClick={() => viewAdhesion(adhesion._id)}
                                className="inline-flex gap-2 items-center py-1.5 pl-1.5 pr-2 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                                title="Voir les détails du service"
                              >
                                <EyeIcon className="h-3 w-3" />
                                Voir adhésion
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <UserIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">Aucun utilisateur trouvé</h3>
              <p className="mt-1 text-sm text-slate-500">Essayez de modifier vos filtres de recherche.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modale d'édition */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  Modifier l'utilisateur
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  {editingUser.prenom} {editingUser.nom}
                </p>
              </div>
              <button
                onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Type de personne */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select
                    value={editForm.typePersonne || 'personne_physique'}
                    onChange={(e) => setEditForm(prev => ({ ...prev, typePersonne: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="personne_physique">Personne physique</option>
                    <option value="association">Association</option>
                    <option value="scea">SCEA</option>
                    <option value="earl">EARL</option>
                    <option value="etablissement_public">Établissement public</option>
                  </select>
                </div>
                
                {/* Civilité */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Civilité</label>
                  <select
                    value={editForm.designation || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, designation: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">-- Sélectionner --</option>
                    <option value="M.">M.</option>
                    <option value="Mme">Mme</option>
                  </select>
                </div>

                {/* Raison sociale - visible pour personne morale */}
                {['association', 'scea', 'earl', 'etablissement_public'].includes(editForm.typePersonne) && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Raison sociale</label>
                    <input
                      type="text"
                      value={editForm.raisonSociale || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, raisonSociale: e.target.value }))}
                      placeholder="Nom de l'association, SCEA, EARL ou établissement"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}

                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {['association', 'scea', 'earl', 'etablissement_public'].includes(editForm.typePersonne) ? 'Nom du représentant légal' : 'Nom'}
                  </label>
                  <input
                    type="text"
                    value={editForm.nom}
                    onChange={(e) => setEditForm(prev => ({ ...prev, nom: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Prénom */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {['association', 'scea', 'earl', 'etablissement_public'].includes(editForm.typePersonne) ? 'Prénom du représentant légal' : 'Prénom'}
                  </label>
                  <input
                    type="text"
                    value={editForm.prenom}
                    onChange={(e) => setEditForm(prev => ({ ...prev, prenom: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email
                    {!isSuperAdmin && !permissions?.users?.modifierEmail && (
                      <span className="ml-2 text-xs text-slate-500">(non modifiable)</span>
                    )}
                  </label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!isSuperAdmin && !permissions?.users?.modifierEmail}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      !isSuperAdmin && !permissions?.users?.modifierEmail ? 'bg-slate-100 cursor-not-allowed text-slate-500' : ''
                    }`}
                  />
                </div>

                {/* Date de naissance */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date de naissance</label>
                  <input
                    type="date"
                    value={editForm.dateNaissance}
                    onChange={(e) => setEditForm(prev => ({ ...prev, dateNaissance: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Téléphone mobile */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone mobile</label>
                  <PhoneInput
                    name="telephoneMobile"
                    id="editTelephoneMobile"
                    value={editForm.telephoneMobile || ''}
                    onChange={(value) => setEditForm(prev => ({ ...prev, telephoneMobile: value }))}
                    placeholder=""
                  />
                </div>

                {/* Téléphone fixe */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone fixe</label>
                  <PhoneInput
                    name="telephone"
                    id="editTelephone"
                    value={editForm.telephone || ''}
                    onChange={(value) => setEditForm(prev => ({ ...prev, telephone: value }))}
                    placeholder=""
                  />
                </div>

                {/* NAPI */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">NAPI</label>
                  <input
                    type="text"
                    value={editForm.napi}
                    onChange={(e) => setEditForm(prev => ({ ...prev, napi: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* SIRET */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">SIRET</label>
                  <input
                    type="text"
                    value={editForm.siret}
                    maxLength="14"
                    onChange={(e) => setEditForm(prev => ({ ...prev, siret: normalizeSiret(e.target.value) }))}
                    placeholder="14 chiffres exactement"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {editForm.siret && editForm.siret.length !== 14 && (
                    <p className="mt-1 text-xs text-red-500">{SIRET_ERROR}</p>
                  )}
                </div>

                {/* Adresse */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
                  <AddressAutocomplete
                    initialValue={editForm.adresse?.rue || ''}
                    placeholder="Commencez à taper l'adresse..."
                    inputClassName="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onAddressSelect={(address) => {
                      setEditForm(prev => ({
                        ...prev,
                        adresse: {
                          rue: address.rue,
                          codePostal: address.codePostal,
                          ville: address.ville,
                        }
                      }));
                    }}
                  />
                </div>

                {/* Code postal */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Code postal</label>
                  <input
                    type="text"
                    value={editForm.adresse?.codePostal || ''}
                    onChange={(e) => setEditForm(prev => ({ 
                      ...prev, 
                      adresse: { ...prev.adresse, codePostal: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Ville */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
                  <input
                    type="text"
                    value={editForm.adresse?.ville || ''}
                    onChange={(e) => setEditForm(prev => ({ 
                      ...prev, 
                      adresse: { ...prev.adresse, ville: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveUser}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale des adhésions */}
      {showAdhesionsModal && selectedUserAdhesions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  Adhésions de {selectedUserAdhesions.user.prenom} {selectedUserAdhesions.user.nom}
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  {selectedUserAdhesions.adhesions.length} adhésion{selectedUserAdhesions.adhesions.length > 1 ? 's' : ''} au total
                </p>
              </div>
              <button
                onClick={() => { setShowAdhesionsModal(false); setSelectedUserAdhesions(null); }}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3">
                {selectedUserAdhesions.adhesions
                  .sort((a, b) => b.annee - a.annee)
                  .map((adhesion, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            adhesion.organisme === 'ADAR' ? 'bg-blue-50' :
                            adhesion.organisme === 'SAR' ? 'bg-green-50' : 'bg-pink-50'
                          }`}>
                            <span className={`font-semibold text-sm ${
                              adhesion.organisme === 'ADAR' ? 'text-[#0B4395]' :
                              adhesion.organisme === 'SAR' ? 'text-green-700' : 'text-pink-700'
                            }`}>
                              {adhesion.annee}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div className={`font-medium ${
                            adhesion.organisme === 'ADAR' ? 'text-[#0B4395]' :
                            adhesion.organisme === 'SAR' ? 'text-green-700' : 'text-pink-700'
                          }`}>
                            {adhesion.organisme}
                          </div>
                          <div className="text-sm text-slate-500">
                            Année {adhesion.annee}
                          </div>
                        </div>
                      </div>
                      <div>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(adhesion.status)}`}
                        >
                          {getStatusIcon(adhesion.status)} {getStatusLabel(adhesion.status)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end">
              <button
                onClick={() => { setShowAdhesionsModal(false); setSelectedUserAdhesions(null); }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
